import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, ApiError, errorMessage } from '../../shared/api/client';
import type { MediaLibrary, PresentationDetail, PresentationItemInput, PresentationItemKind, SetlistSong, Song, WorshipService } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { serviceWhen } from './PresentationsPage';
import { sections } from '../songs/lyrics';
import { BACKGROUNDS } from './backgrounds';
import { localApi, type LocalFile } from './local';

export const KIND_LABEL: Record<PresentationItemKind, string> = { lyrics: 'Canto', text: 'Texto', scripture: 'Biblia', media: 'Imagen, video, audio o PDF' };

interface Draft extends PresentationItemInput {
  key: number;
}

let nextKey = 1;
const blank = (kind: PresentationItemKind): Draft => ({ key: nextKey++, kind, title: '', body: null, passage: null, resourceId: null, stageNotes: null, arrangement: null, backgroundPreset: null, localFile: null, youtube: null, translation: null, camera: null });

interface Loaded {
  presentation: PresentationDetail | null;
  services: WorshipService[];
  library: MediaLibrary['items'];
}

/** Crear o editar el programa de una presentación. */
export function PresentationBuilderPage() {
  const { presentationId } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync<Loaded>(
    async (signal) => {
      if (!churchId) throw new Error('Sin contexto');
      const [presentation, services, library] = await Promise.all([
        presentationId ? api.get<PresentationDetail>(`/presentation/churches/${churchId}/presentations/${presentationId}`, signal) : Promise.resolve(null),
        api.get<{ items: WorshipService[] }>(`/worship/churches/${churchId}/services?from=${encodeURIComponent(new Date(Date.now() - 86_400_000).toISOString())}`, signal).then((r) => r.items.filter((s) => s.status !== 'canceled')),
        api.get<MediaLibrary>(`/media/churches/${churchId}/resources`, signal).then((r) => r.items).catch(() => []),
      ]);
      return { presentation, services, library };
    },
    [churchId, presentationId],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to={presentationId ? `/app/presentaciones/${presentationId}` : '/app/presentaciones'}>← {presentationId ? 'Volver a la consola' : 'Proyectar'}</Link>
      </p>
      <header className="page__header">
        <h1>{presentationId ? 'Editar presentación' : 'Nueva presentación'}</h1>
      </header>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? <Builder churchId={churchId} loaded={data.data} /> : null}
    </div>
  );
}

function Builder({ churchId, loaded }: { churchId: string; loaded: Loaded }) {
  const navigate = useNavigate();
  const p = loaded.presentation;
  const [items, setItems] = useState<Draft[]>(() => (p ? p.itemsList.map((i) => ({ key: nextKey++, kind: i.kind, title: i.title, body: i.body, passage: i.passage, resourceId: i.resourceId, stageNotes: i.stageNotes, arrangement: i.arrangement ?? null, backgroundPreset: i.backgroundPreset ?? null, localFile: i.localFile ?? null, youtube: i.youtube ?? null, translation: i.translation ?? null, camera: i.camera ?? null })) : [blank('lyrics')]));
  // Cada elemento se ve en una línea; se abre con «Editar». Los que se acaban de agregar salen abiertos (#56).
  const [open, setOpen] = useState<Set<number>>(() => new Set(p ? [] : items.map((i) => i.key)));
  const seen = useRef(new Set(items.map((i) => i.key)));
  useEffect(() => {
    const added = items.filter((i) => !seen.current.has(i.key)).map((i) => i.key);
    if (added.length === 0) return;
    for (const k of added) seen.current.add(k);
    setOpen((s) => new Set([...s, ...added]));
  }, [items]);
  const toggle = (key: number) => setOpen((s) => (s.has(key) ? new Set([...s].filter((k) => k !== key)) : new Set([...s, key])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);
  // En la app KEORA Multimedia: los archivos de la carpeta de esta computadora (no se suben).
  const [local] = useState(localApi);
  const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
  useEffect(() => {
    void local?.list().then(setLocalFiles).catch(() => undefined);
  }, [local]);

  const update = (key: number, patch: Partial<Draft>) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  const move = (index: number, delta: -1 | 1) =>
    setItems((list) => {
      const next = [...list];
      const [it] = next.splice(index, 1);
      next.splice(index + delta, 0, it!);
      return next;
    });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const title = String(form.get('title') ?? '').trim();
    if (title.length < 3) return setError('Escribe un nombre de al menos 3 letras.');
    const body = {
      title,
      serviceId: String(form.get('serviceId') ?? '') || null,
      items: items.map(({ key: _key, ...i }) => ({ ...i, title: i.title.trim() || KIND_LABEL[i.kind] })),
    };
    setBusy(true);
    setError(null);
    setErrorField(null);
    try {
      const saved = p
        ? await api.put<PresentationDetail>(`/presentation/churches/${churchId}/presentations/${p.id}`, body)
        : await api.post<PresentationDetail>(`/presentation/churches/${churchId}/presentations`, body);
      navigate(`/app/presentaciones/${saved.id}`, { replace: true });
    } catch (e) {
      setError(errorMessage(e));
      if (e instanceof ApiError && typeof e.details?.field === 'string') {
        const field = e.details.field;
        setErrorField(field);
        // El elemento con el error se abre para corregirlo.
        const bad = /^items\.(\d+)\./.exec(field);
        const key = bad ? items[Number(bad[1])]?.key : undefined;
        if (key !== undefined) setOpen((s) => new Set([...s, key]));
      }
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit} noValidate>
      <Card>
        <Field label="Nombre" name="title" required minLength={3} maxLength={120} defaultValue={p?.title} placeholder="Culto dominical" />
        <div className="field">
          <label htmlFor="serviceId">Culto (opcional)</label>
          <select id="serviceId" name="serviceId" defaultValue={p?.service?.id ?? ''}>
            <option value="">Sin culto</option>
            {loaded.services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title} · {serviceWhen(s.startsAt)}
              </option>
            ))}
          </select>
        </div>
      </Card>
      <ol className="program-edit">
        {items.map((it, index) => {
          const prefix = `items.${index}.`;
          const invalid = errorField?.startsWith(prefix) ? errorField.slice(prefix.length) : null;
          const id = (name: string) => `item-${it.key}-${name}`;
          const tools = (
            <div className="program-edit__tools">
              <Button type="button" variant="ghost" disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Subir elemento ${index + 1}`}>
                ↑
              </Button>
              <Button type="button" variant="ghost" disabled={index === items.length - 1} onClick={() => move(index, 1)} aria-label={`Bajar elemento ${index + 1}`}>
                ↓
              </Button>
              <Button type="button" variant="ghost" aria-expanded={open.has(it.key)} onClick={() => toggle(it.key)} aria-label={`${open.has(it.key) ? 'Cerrar' : 'Editar'} elemento ${index + 1}`}>
                {open.has(it.key) ? 'Listo' : 'Editar'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setItems((list) => list.filter((x) => x.key !== it.key))} aria-label={`Quitar elemento ${index + 1}`}>
                Quitar
              </Button>
            </div>
          );
          if (!open.has(it.key))
            return (
              <li key={it.key} className={`card program-edit__item program-edit__item--line${invalid ? ' program-edit__item--invalid' : ''}`}>
                <div className="program-edit__head">
                  <span className="badge">{index + 1}</span>
                  <span className="program-edit__line">
                    {KIND_LABEL[it.kind]} · {it.title.trim() || it.passage || 'Sin nombre'}
                  </span>
                  {tools}
                </div>
              </li>
            );
          return (
            <li key={it.key} className="card program-edit__item">
              <div className="program-edit__head">
                <span className="badge">{index + 1}</span>
                <label className="visually-hidden" htmlFor={id('kind')}>
                  Tipo del elemento {index + 1}
                </label>
                <select id={id('kind')} value={it.kind} onChange={(e) => update(it.key, { kind: e.target.value as PresentationItemKind })}>
                  {(Object.keys(KIND_LABEL) as PresentationItemKind[]).map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABEL[k]}
                    </option>
                  ))}
                </select>
                {tools}
              </div>
              <div className="field">
                <label htmlFor={id('title')}>{it.kind === 'lyrics' ? 'Nombre del canto' : 'Título'}</label>
                <input id={id('title')} value={it.title} maxLength={120} onChange={(e) => update(it.key, { title: e.target.value })} />
              </div>
              {it.kind === 'lyrics' || it.kind === 'text' ? (
                <div className="field">
                  <label htmlFor={id('body')}>{it.kind === 'lyrics' ? 'Letra' : 'Texto'}</label>
                  <textarea id={id('body')} rows={6} maxLength={20_000} value={it.body ?? ''} aria-invalid={invalid === 'body'} aria-describedby={id('hint')} onChange={(e) => update(it.key, { body: e.target.value || null })} />
                  <p id={id('hint')} className="field__hint">
                    Cada estrofa es una diapositiva: sepáralas con una línea en blanco.{it.kind === 'lyrics' ? ' Nombra las partes con [Coro], [Estrofa 1]… para poder ordenarlas.' : ''}
                  </p>
                  {it.kind === 'lyrics' ? <p className="field__hint program-edit__copy">Esta letra es una copia del canto. Si lo corriges en Cantos, aquí no cambia; corrígelo también aquí.</p> : null}
                </div>
              ) : null}
              {it.kind === 'lyrics' ? <Arrangement body={it.body ?? ''} value={it.arrangement ?? []} invalid={invalid === 'arrangement'} onChange={(arrangement) => update(it.key, { arrangement: arrangement.length > 0 ? arrangement : null })} /> : null}
              {it.kind === 'scripture' ? (
                <div className="field">
                  <label htmlFor={id('passage')}>Cita</label>
                  <input id={id('passage')} value={it.passage ?? ''} maxLength={80} placeholder="Juan 3:16-18" aria-invalid={invalid === 'passage'} onChange={(e) => update(it.key, { passage: e.target.value || null })} />
                </div>
              ) : null}
              <div className="field">
                <label htmlFor={id('resource')}>{it.kind === 'media' ? 'Archivo de Multimedia' : 'Fondo (opcional)'}</label>
                <select
                  id={id('resource')}
                  value={it.resourceId ?? (it.localFile ? `local:${it.localFile}` : it.backgroundPreset ? `fondo:${it.backgroundPreset}` : '')}
                  aria-invalid={invalid === 'resourceId' || invalid === 'localFile'}
                  onChange={(e) => {
                    const v = e.target.value;
                    update(
                      it.key,
                      v.startsWith('fondo:')
                        ? { resourceId: null, localFile: null, youtube: null, backgroundPreset: v.slice(6) }
                        : v.startsWith('local:')
                          ? { resourceId: null, backgroundPreset: null, youtube: null, localFile: v.slice(6), title: it.title || v.slice(6).replace(/\.[^.]+$/, '').slice(0, 120) }
                          : { resourceId: v || null, localFile: null, youtube: null, backgroundPreset: null },
                    );
                  }}
                >
                  <option value="">{it.kind === 'media' ? 'Elige de la biblioteca' : 'El del tema'}</option>
                  {it.kind !== 'media' ? (
                    <optgroup label="Fondos KEORA">
                      {BACKGROUNDS.map((b) => (
                        <option key={b.key} value={`fondo:${b.key}`}>
                          {b.name}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                  {it.kind === 'media' && (localFiles.length > 0 || (it.localFile && !localFiles.some((f) => f.name === it.localFile))) ? (
                    <optgroup label="En esta computadora">
                      {it.localFile && !localFiles.some((f) => f.name === it.localFile) ? <option value={`local:${it.localFile}`}>{it.localFile}</option> : null}
                      {localFiles.map((f) => (
                        <option key={f.name} value={`local:${f.name}`}>
                          {f.name}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                  {loaded.library
                    .filter((r) => it.kind === 'media' || r.kind === 'image')
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                        {r.folder ? ` (${r.folder})` : ''}
                      </option>
                    ))}
                </select>
                {it.kind === 'media' ? (
                  <>
                    <label htmlFor={id('youtube')}>O un video de YouTube (se reproduce desde YouTube, sin descargarlo)</label>
                    <input
                      id={id('youtube')}
                      type="url"
                      value={it.youtube ? (it.youtube.length === 11 ? `https://youtu.be/${it.youtube}` : it.youtube) : ''}
                      placeholder="https://www.youtube.com/watch?v=…"
                      aria-invalid={invalid === 'youtube'}
                      onChange={(e) => update(it.key, e.target.value.trim() ? { youtube: e.target.value.trim(), resourceId: null, localFile: null, backgroundPreset: null } : { youtube: null })}
                    />
                  </>
                ) : null}
                {it.kind === 'media' && local ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() =>
                      void local.add().then((files) => {
                        const added = files.find((f) => !localFiles.some((o) => o.name === f.name));
                        setLocalFiles(files);
                        if (added) update(it.key, { resourceId: null, backgroundPreset: null, localFile: added.name, title: it.title || added.name.replace(/\.[^.]+$/, '').slice(0, 120) });
                      })
                    }
                  >
                    Agregar archivo de esta computadora (no se sube al servidor)
                  </button>
                ) : null}
                {it.localFile && !local ? <p className="small muted">Este archivo está en la computadora de proyección; solo se ve en la app KEORA Multimedia.</p> : null}
              </div>
              <div className="field">
                <label htmlFor={id('notes')}>Notas para el escenario (opcional)</label>
                <input id={id('notes')} value={it.stageNotes ?? ''} maxLength={1000} placeholder="Solo las ve el equipo" onChange={(e) => update(it.key, { stageNotes: e.target.value || null })} />
              </div>
            </li>
          );
        })}
      </ol>
      <SermonImport churchId={churchId} onAdd={(add) => setItems((list) => [...list.filter((i) => i.title.trim() || i.body || i.passage || i.resourceId), ...add])} />
      <SongImport churchId={churchId} onAdd={(songs) => setItems((list) => [...list.filter((i) => i.kind !== 'lyrics' || i.title.trim() || i.body), ...songs.map((x) => ({ ...blank('lyrics'), title: x.title.slice(0, 120), body: x.lyrics.trim() || null }))])} />
      <div className="actions actions--wrap">
        {(Object.keys(KIND_LABEL) as PresentationItemKind[]).map((k) => (
          <Button key={k} type="button" variant="secondary" onClick={() => setItems((list) => [...list, blank(k)])}>
            + {KIND_LABEL[k]}
          </Button>
        ))}
      </div>
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={busy}>
          Guardar
        </Button>
      </div>
    </form>
  );
}

type SermonProgram = { passages: string[]; materials?: { id: string; name: string }[] };

/** Traer la Predicación del culto: sus versículos y su material de apoyo, en orden. */
function SermonImport({ churchId, onAdd }: { churchId: string; onAdd: (items: Draft[]) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Card title="Predicación">
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions actions--wrap">
        <Button
          type="button"
          variant="secondary"
          busy={busy}
          onClick={async () => {
            const serviceId = (document.getElementById('serviceId') as HTMLSelectElement | null)?.value;
            if (!serviceId) return setError('Primero elige el culto arriba.');
            setError(null);
            setBusy(true);
            try {
              const p = await api.get<SermonProgram>(`/worship/churches/${churchId}/services/${serviceId}/program`);
              const items = [
                ...p.passages.map((passage) => ({ ...blank('scripture'), title: passage.slice(0, 120), passage })),
                ...(p.materials ?? []).map((m) => ({ ...blank('media'), title: m.name.slice(0, 120), resourceId: m.id })),
              ];
              if (items.length === 0) setError('La Predicación de ese culto todavía no tiene versículos ni material de apoyo.');
              else onAdd(items);
            } catch (e) {
              setError(errorMessage(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Traer versículos y material de la Predicación
        </Button>
      </div>
    </Card>
  );
}

/**
 * Orden de las partes de un canto: se toca una parte para agregarla al final y una del orden para
 * quitarla. Sin orden, el canto sale como está escrito.
 */
function Arrangement({ body, value, invalid, onChange }: { body: string; value: string[]; invalid: boolean; onChange: (order: string[]) => void }) {
  const names = [...new Set(sections(body).flatMap((s) => (s.name ? [s.name] : [])))];
  if (names.length === 0) return null;
  const chorus = names.find((n) => /coro/i.test(n));
  const verses = names.filter((n) => n !== chorus);
  return (
    <div className="field arrangement" aria-invalid={invalid}>
      <p className="arrangement__label">Orden de las partes</p>
      <div className="arrangement__order" aria-live="polite">
        {value.length === 0 ? <span className="muted small">Como está escrita</span> : null}
        {value.map((n, i) => (
          <button key={i} type="button" className="chip chip--active" aria-label={`Quitar ${n} (lugar ${i + 1})`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
            {n} ×
          </button>
        ))}
      </div>
      <div className="arrangement__parts">
        {names.map((n) => (
          <button key={n} type="button" className="chip" onClick={() => onChange([...value, n])}>
            + {n}
          </button>
        ))}
      </div>
      <div className="actions actions--wrap">
        {chorus && verses.length > 0 ? (
          <Button type="button" variant="ghost" onClick={() => onChange(verses.flatMap((v) => [v, chorus]))}>
            {chorus} después de cada parte
          </Button>
        ) : null}
        {value.length > 0 ? (
          <Button type="button" variant="ghost" onClick={() => onChange([])}>
            Como está escrita
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Traer letras desde la biblioteca de cantos: los del culto elegido o uno por uno. */
function SongImport({ churchId, onAdd }: { churchId: string; onAdd: (songs: { title: string; lyrics: string }[]) => void }) {
  const library = useAsync((signal) => api.get<{ items: Song[] }>(`/worship/churches/${churchId}/songs`, signal).then((r) => r.items).catch(() => [] as Song[]), [churchId]);
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);
  const songs = library.status === 'ready' ? library.data : [];
  return (
    <Card title="Cantos de Alabanza">
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions actions--wrap">
        <Button
          type="button"
          variant="secondary"
          onClick={async () => {
            const serviceId = (document.getElementById('serviceId') as HTMLSelectElement | null)?.value;
            if (!serviceId) return setError('Primero elige el culto arriba.');
            setError(null);
            try {
              const set = await api.get<{ items: SetlistSong[] }>(`/worship/churches/${churchId}/services/${serviceId}/songs`);
              if (set.items.length === 0) return setError('Alabanza todavía no elige los cantos de ese culto.');
              onAdd(set.items);
            } catch (e) {
              setError(errorMessage(e));
            }
          }}
        >
          Traer los cantos del culto
        </Button>
      </div>
      {songs.length > 0 ? (
        <div className="family-add">
          <div className="field">
            <label htmlFor="import-song">O agrega un canto de la biblioteca</label>
            <select id="import-song" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Elige un canto…</option>
              {songs.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.title}
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={!pick}
            onClick={() => {
              const x = songs.find((y) => y.id === pick);
              if (x) onAdd([x]);
              setPick('');
            }}
          >
            Agregar letra
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
